import { useState, useEffect } from 'react';
import { PlayIcon, ArrowRightIcon } from '@heroicons/react/24/outline';
import type { YouTubeVideo } from '@mermaids/shared';
import YouTubeShortsCard from './YouTubeShortsCard';
import YouTubeModal from './YouTubeModal';
import { API_ENDPOINTS } from '../../config/constants';
import {
  shuffleAndPick,
  YOUTUBE_CHANNEL_NAME,
  YOUTUBE_CHANNEL_URL,
} from './utils';

const YouTubeShortsSection = () => {
  const [shorts, setShorts] = useState<YouTubeVideo[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedVideoId, setSelectedVideoId] = useState<string | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  useEffect(() => {
    fetchShorts();
  }, []);

  const fetchShorts = async () => {
    try {
      const response = await fetch(
        `${API_ENDPOINTS.youtube}/shorts?maxResults=4`
      );

      // Check if response is actually JSON
      const contentType = response.headers.get('content-type');
      if (!contentType || !contentType.includes('application/json')) {
        // Fallback to mock Shorts data
        const mockShorts = [
          {
            id: 'short1',
            title: '🏝️ Paradise Found! #MermaidMoments #TravelShorts',
            description:
              "Quick glimpse of the most beautiful beach I've ever seen! Follow for more travel magic ✨ #MermaidsOfTheMoment",
            thumbnail: {
              default:
                'https://images.unsplash.com/photo-1559827260-dc66d52bef19?w=240&h=426&fit=crop',
              medium:
                'https://images.unsplash.com/photo-1559827260-dc66d52bef19?w=320&h=568&fit=crop',
              high: 'https://images.unsplash.com/photo-1559827260-dc66d52bef19?w=480&h=852&fit=crop',
            },
            publishedAt: new Date(
              Date.now() - Math.random() * 14 * 24 * 60 * 60 * 1000
            ).toISOString(),
            channelTitle: YOUTUBE_CHANNEL_NAME,
            duration: `0:${Math.floor(Math.random() * 45) + 15}`,
            viewCount: (Math.floor(Math.random() * 30000) + 5000).toString(),
            likeCount: (Math.floor(Math.random() * 1500) + 300).toString(),
            commentCount: (Math.floor(Math.random() * 200) + 50).toString(),
            isShort: true,
          },
          {
            id: 'short2',
            title: '🌺 Local Food Adventure #FoodieTravel #Shorts',
            description:
              'Trying the most amazing local street food! The flavors are incredible 🤤 #MermaidsOfTheMoment',
            thumbnail: {
              default:
                'https://images.unsplash.com/photo-1555396273-367ea4eb4db5?w=240&h=426&fit=crop',
              medium:
                'https://images.unsplash.com/photo-1555396273-367ea4eb4db5?w=320&h=568&fit=crop',
              high: 'https://images.unsplash.com/photo-1555396273-367ea4eb4db5?w=480&h=852&fit=crop',
            },
            publishedAt: new Date(
              Date.now() - Math.random() * 14 * 24 * 60 * 60 * 1000
            ).toISOString(),
            channelTitle: YOUTUBE_CHANNEL_NAME,
            duration: `0:${Math.floor(Math.random() * 35) + 25}`,
            viewCount: (Math.floor(Math.random() * 25000) + 3000).toString(),
            likeCount: (Math.floor(Math.random() * 1200) + 200).toString(),
            commentCount: (Math.floor(Math.random() * 150) + 30).toString(),
            isShort: true,
          },
          {
            id: 'short3',
            title: '⛰️ Sunrise Hike #AdventureShorts #MermaidLife',
            description:
              'Worth the 4AM wake up call! This sunrise view is pure magic 🌅 #MermaidsOfTheMoment',
            thumbnail: {
              default:
                'https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=240&h=426&fit=crop',
              medium:
                'https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=320&h=568&fit=crop',
              high: 'https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=480&h=852&fit=crop',
            },
            publishedAt: new Date(
              Date.now() - Math.random() * 14 * 24 * 60 * 60 * 1000
            ).toISOString(),
            channelTitle: YOUTUBE_CHANNEL_NAME,
            duration: `0:${Math.floor(Math.random() * 28) + 20}`,
            viewCount: (Math.floor(Math.random() * 35000) + 8000).toString(),
            likeCount: (Math.floor(Math.random() * 1800) + 400).toString(),
            commentCount: (Math.floor(Math.random() * 250) + 60).toString(),
            isShort: true,
          },
          {
            id: 'short4',
            title: '🐠 Underwater Magic #DivingShorts #Ocean',
            description:
              'Swimming with the most colorful fish! Underwater paradise 🌊 #MermaidsOfTheMoment',
            thumbnail: {
              default:
                'https://images.unsplash.com/photo-1583212292454-1fe6229603b7?w=240&h=426&fit=crop',
              medium:
                'https://images.unsplash.com/photo-1583212292454-1fe6229603b7?w=320&h=568&fit=crop',
              high: 'https://images.unsplash.com/photo-1583212292454-1fe6229603b7?w=480&h=852&fit=crop',
            },
            publishedAt: new Date(
              Date.now() - Math.random() * 14 * 24 * 60 * 60 * 1000
            ).toISOString(),
            channelTitle: YOUTUBE_CHANNEL_NAME,
            duration: `0:${Math.floor(Math.random() * 40) + 20}`,
            viewCount: (Math.floor(Math.random() * 40000) + 10000).toString(),
            likeCount: (Math.floor(Math.random() * 2000) + 500).toString(),
            commentCount: (Math.floor(Math.random() * 300) + 80).toString(),
            isShort: true,
          },
          {
            id: 'short5',
            title: '🏖️ Secret Beach Discovery #HiddenGems #Shorts',
            description:
              'Found this amazing secret beach! No crowds, just pure paradise 🏝️ #MermaidsOfTheMoment',
            thumbnail: {
              default:
                'https://images.unsplash.com/photo-1507525428034-b723cf961d3e?w=240&h=426&fit=crop',
              medium:
                'https://images.unsplash.com/photo-1507525428034-b723cf961d3e?w=320&h=568&fit=crop',
              high: 'https://images.unsplash.com/photo-1507525428034-b723cf961d3e?w=480&h=852&fit=crop',
            },
            publishedAt: new Date(
              Date.now() - Math.random() * 14 * 24 * 60 * 60 * 1000
            ).toISOString(),
            channelTitle: YOUTUBE_CHANNEL_NAME,
            duration: `0:${Math.floor(Math.random() * 50) + 20}`,
            viewCount: (Math.floor(Math.random() * 28000) + 6000).toString(),
            likeCount: (Math.floor(Math.random() * 1400) + 350).toString(),
            commentCount: (Math.floor(Math.random() * 180) + 40).toString(),
            isShort: true,
          },
          {
            id: 'short6',
            title: '🧜‍♀️ Mermaid Swimming Tips #Tutorial #Shorts',
            description:
              'Quick tips for swimming like a real mermaid! 🧜‍♀️✨ #MermaidsOfTheMoment #MermaidLife',
            thumbnail: {
              default:
                'https://images.unsplash.com/photo-1550534791-2677533605ab?w=240&h=426&fit=crop',
              medium:
                'https://images.unsplash.com/photo-1550534791-2677533605ab?w=320&h=568&fit=crop',
              high: 'https://images.unsplash.com/photo-1550534791-2677533605ab?w=480&h=852&fit=crop',
            },
            publishedAt: new Date(
              Date.now() - Math.random() * 14 * 24 * 60 * 60 * 1000
            ).toISOString(),
            channelTitle: YOUTUBE_CHANNEL_NAME,
            duration: `0:${Math.floor(Math.random() * 45) + 25}`,
            viewCount: (Math.floor(Math.random() * 45000) + 12000).toString(),
            likeCount: (Math.floor(Math.random() * 2200) + 600).toString(),
            commentCount: (Math.floor(Math.random() * 320) + 100).toString(),
            isShort: true,
          },
        ];

        setShorts(shuffleAndPick(mockShorts, 4));
        setLoading(false);
        return;
      }

      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          const shorts = data.data || [];
          // Extra security: Filter to only show shorts from "Mermaids Of The Moment" channel
          const channelLower = YOUTUBE_CHANNEL_NAME.toLowerCase();
          const filteredShorts = shorts.filter(
            (short: YouTubeVideo) =>
              short.channelTitle === YOUTUBE_CHANNEL_NAME ||
              short.channelTitle.toLowerCase().includes(channelLower) ||
              short.channelTitle
                .toLowerCase()
                .includes(channelLower.replace(/\s+/g, ''))
          );
          setShorts(filteredShorts);
        } else {
          throw new Error(data.error || 'Failed to fetch shorts');
        }
      } else {
        throw new Error(`HTTP ${response.status}`);
      }
    } catch {
      setError('Unable to load Shorts at the moment');
      // Set fallback Shorts - only use when YouTube API is unavailable
      // These are placeholder cards that prompt users to visit your actual channel
      setShorts([
        {
          id: 'mermaids_short_placeholder_1',
          title: '🧜‍♀️ Visit @mermaidsofthemoment for Travel Shorts!',
          description:
            'Check out our YouTube channel for amazing travel shorts and quick adventure moments! Subscribe to @mermaidsofthemoment for exclusive content.',
          thumbnail: {
            default:
              'https://images.unsplash.com/photo-1544551763-46a013bb70d5?w=240&h=426&fit=crop&q=80',
            medium:
              'https://images.unsplash.com/photo-1544551763-46a013bb70d5?w=320&h=568&fit=crop&q=80',
            high: 'https://images.unsplash.com/photo-1544551763-46a013bb70d5?w=480&h=852&fit=crop&q=80',
          },
          publishedAt: new Date().toISOString(),
          channelTitle: 'Mermaids Of The Moment',
          duration: 'Visit',
          viewCount: 'Visit',
          likeCount: 'Channel',
          commentCount: 'Now!',
          isShort: true,
        },
        {
          id: 'mermaids_short_placeholder_2',
          title: '🌊 Quick Travel Moments & Tips',
          description:
            'Get instant travel inspiration! Follow @mermaidsofthemoment for travel shorts, destination highlights, and adventure moments.',
          thumbnail: {
            default:
              'https://images.unsplash.com/photo-1507525428034-b723cf961d3e?w=240&h=426&fit=crop&q=80',
            medium:
              'https://images.unsplash.com/photo-1507525428034-b723cf961d3e?w=320&h=568&fit=crop&q=80',
            high: 'https://images.unsplash.com/photo-1507525428034-b723cf961d3e?w=480&h=852&fit=crop&q=80',
          },
          publishedAt: new Date().toISOString(),
          channelTitle: 'Mermaids Of The Moment',
          duration: 'Visit',
          viewCount: 'Visit',
          likeCount: 'Channel',
          commentCount: 'Now!',
          isShort: true,
        },
      ]);
    } finally {
      setLoading(false);
    }
  };

  const handleVideoClick = (videoId: string) => {
    // Play Shorts inside modal; placeholders redirect only
    if (videoId.includes('placeholder')) {
      window.open(YOUTUBE_CHANNEL_URL, '_blank');
      return;
    }
    setSelectedVideoId(videoId);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedVideoId(null);
  };

  if (loading) {
    return (
      <section className="py-12 lg:py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8">
            <div className="flex items-center justify-center mb-4 animate-pulse">
              <div className="h-8 w-48 bg-stone-200 rounded-lg"></div>
            </div>
            <div className="h-5 w-64 bg-stone-200 rounded-lg mx-auto"></div>
          </div>

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            {[...Array(4)].map((_, i) => (
              <div
                key={i}
                className="bg-stone-50 rounded-xl border border-stone-200 overflow-hidden animate-pulse"
              >
                <div className="aspect-[9/16] max-h-72 bg-stone-200"></div>
                <div className="p-3">
                  <div className="h-4 bg-stone-200 rounded mb-2"></div>
                  <div className="h-3 bg-stone-200 rounded w-3/4"></div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    );
  }

  if (shorts.length === 0) {
    return null; // Don't show the section if no shorts are available
  }

  return (
    <>
      <section className="py-12 lg:py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Section Header */}
          <div className="text-center mb-10">
            <h2 className="text-2xl font-bold text-stone-900 mb-2">
              Quick Moments
            </h2>
            <p className="text-stone-500 max-w-2xl mx-auto">
              Bite-sized adventures and quick glimpses of amazing destinations
            </p>
          </div>

          {error && (
            <div className="text-center mb-8">
              <div className="inline-flex items-center px-4 py-2 bg-stone-50 border border-stone-200 rounded-lg text-stone-500 text-sm">
                <span>Shorts loading with sample content</span>
              </div>
            </div>
          )}

          {/* Shorts Grid - Mobile-first responsive */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-10">
            {shorts.map((short, index) => (
              <YouTubeShortsCard
                key={short.id}
                video={short}
                index={index}
                onVideoClick={handleVideoClick}
              />
            ))}
          </div>

          {/* Call to Action */}
          <div className="text-center">
            <div className="inline-flex flex-col sm:flex-row items-center gap-5 p-6 bg-stone-900 rounded-2xl text-white shadow-xl">
              <div className="text-center sm:text-left">
                <h3 className="text-lg font-semibold mb-1">
                  More Quick Adventures
                </h3>
                <p className="text-sm text-stone-400">
                  Follow us for daily Shorts and tips
                </p>
              </div>
              <a
                href={YOUTUBE_CHANNEL_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center px-5 py-2.5 bg-red-600 text-white font-medium rounded-lg hover:bg-red-700 transition-colors"
              >
                <PlayIcon className="w-5 h-5 mr-2" />
                Watch More Shorts
                <ArrowRightIcon className="ml-2 h-4 w-4" />
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* YouTube Modal */}
      <YouTubeModal
        videoId={selectedVideoId}
        isOpen={isModalOpen}
        onClose={handleCloseModal}
      />
    </>
  );
};

export default YouTubeShortsSection;
